import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AddVehicleProperties, NewVehicle } from 'src/models';
import { EmptyVehicle } from 'src/models/NewVehicle/EmptyVehicle';
import { IStoreState } from 'src/store';

/** Interfaces */
export interface INewVehicleState {
    NewVehicle?: NewVehicle;
    CurrentProperty: AddVehicleProperties;
}

/** Initial State */
export const initialState: INewVehicleState = {
    NewVehicle: {
        vin: '',
        vinValid: false,
        vinOwned: false,
        makeCode: '',
        make: '',
        year: '',
        model: '',
        trim: '',
        color: 'default',
        estMileage: '',
        makeId: '',
        modelId: '',
        option: '',
        optionId: '',
        engine: '',
        engineId: '',
        spgModel: '',
        spgModelId: '',
    },
    CurrentProperty: 'make',
};

export const NewVehicleSlice = createSlice({
    name: 'NewVehicle',
    initialState: initialState as INewVehicleState,
    reducers: {
        clearNewVehicle: (state) => {
            state.NewVehicle = initialState.NewVehicle;
            state.CurrentProperty = 'make';
        },
        addNewVeh: (state, action: PayloadAction<NewVehicle>) => {
            state.NewVehicle = action.payload;
        },
        changeCurrentProperty: (state, action: PayloadAction<AddVehicleProperties>) => {
            state.CurrentProperty = action.payload;
        },
    },
});

export const {
    clearNewVehicle,
    addNewVeh,
    changeCurrentProperty,
} = NewVehicleSlice.actions;

export default NewVehicleSlice.reducer;

/**
 * Dispatches the updated vehicle and advances CurrentProperty. When nextProperty isn't
 * explicitly supplied, derives it from what changed (make → year → model → spgModel), on the
 * assumption the caller is walking forward through the picker. Callers with a reason to skip
 * that default (e.g. a VIN decode, which never needs Model/Option/Engine selection) must pass
 * nextProperty explicitly — see VinInput.tsx and LandingPage.tsx.
 */
export function AddNewVehicle(newVehicle: NewVehicle, nextProperty?: AddVehicleProperties) {
    return (dispatch, getState: () => IStoreState) => {
        const currentVehicle = getState().NewVehicle.NewVehicle;
        let property: AddVehicleProperties = getState().NewVehicle.CurrentProperty;
        if (newVehicle === EmptyVehicle) {
            property = 'make';
        } else if (currentVehicle?.model !== newVehicle.model && newVehicle.model) {
            property = 'spgModel';
        } else if (currentVehicle?.year !== newVehicle.year && newVehicle.year) {
            property = 'model';
        } else if (currentVehicle?.make.toUpperCase() !== newVehicle.make.toUpperCase() && newVehicle.make.toUpperCase()) {
            property = 'year';
        }

        if (nextProperty) {
            property = nextProperty;
        }

        dispatch(addNewVeh(newVehicle));
        dispatch(changeCurrentProperty(property));
    };
}

export function ClearNewVehicle() {
    return (dispatch) => {
        dispatch(clearNewVehicle());
    };
}


// Back-navigation mirrors the forward flow's auto-skip behavior
export function PreviousProperty() {
    return (dispatch, getState: () => IStoreState) => {
        const currentProperty = getState().NewVehicle.CurrentProperty;
        const spgVehicle = getState().SpgVehicle;
        const hasOptionPane = (spgVehicle?.options?.length ?? 0) >= 2;
        const hasEnginePane = (spgVehicle?.engines?.length ?? 0) >= 2;
        const hasSpgModelPane = (spgVehicle?.spgModels?.length ?? 0) >= 2;
        let newProperty: AddVehicleProperties = 'make';
        switch (currentProperty) {
            case 'model': {
                newProperty = 'year';
                break;
            }
            case 'spgModel': {
                newProperty = 'model';
                break;
            }
            case 'option': {
                newProperty = hasSpgModelPane ? 'spgModel' : 'model';
                break;
            }
            case 'engine': {
                if (hasOptionPane) {
                    newProperty = 'option';
                } else if (hasSpgModelPane) {
                    newProperty = 'spgModel';
                } else {
                    newProperty = 'model';
                }
                break;
            }
            case 'color': {
                if (hasEnginePane) {
                    newProperty = 'engine';
                } else if (hasOptionPane) {
                    newProperty = 'option';
                } else if (hasSpgModelPane) {
                    newProperty = 'spgModel';
                } else {
                    newProperty = 'model';
                }
                break;
            }
            case 'year':
            case 'make':
            default: {
                newProperty = 'make';
                break;
            }
        }
        dispatch(changeCurrentProperty(newProperty));
    };
}